import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowPathIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import PermissionsContainer from '../../components/PermissionsContainer';
import { usePermissionsMode } from '../../contexts/PermissionsModeContext';
import FilterBar from '../../components/FilterBar';
import SingleSelect from '../../components/SingleSelect';
import MultiSelect from '../../components/MultiSelect';
import safetySystemService from '../../services/safetySystem.service';
import type { SafetyRegion, UnitRow, UnitsListParams, UnitsView } from '../../types/safetySystem';
import UnitDrawer, { type UnitDrawerTab } from './components/UnitDrawer';
import {
  UNITS_DATE_COLUMNS, VIEW_TITLES, unitsColumnsFor, unitsDefaultHiddenFor, unitsDefaultSorting, unitsRowId,
} from './SafetyUnits.columns';

/**
 * Units grid — port of the V1 Safety "Units" search (search_safety.pl) with the Driver / Truck views
 * plus the Payroll / Certificates / Retention / Load Request views (one endpoint, `view=` selects
 * the column set and row identity). All filter state lives in the URL so the Hub (and bookmarks)
 * can deep-link into a filtered grid:
 *   view, status, region, include_mules, q, search_by, bucket, open=<unitnumber>
 */

const VIEW_OPTIONS: { value: UnitsView; label: string }[] = [
  { value: 'driver', label: 'Driver' },
  { value: 'truck', label: 'Truck' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'cert', label: 'Certificates' },
  { value: 'retention', label: 'Retention' },
  { value: 'loadrequest', label: 'Load Request' },
];
const VIEW_VALUES = new Set<string>(VIEW_OPTIONS.map((o) => o.value));
/** Which drawer tab owns the rows of each view (a payroll row opens the unit on its Payroll tab, …). */
const VIEW_DRAWER_TAB: Record<UnitsView, UnitDrawerTab> = {
  driver: 'driver', truck: 'truck', payroll: 'payroll', cert: 'cert', retention: 'retention', loadrequest: 'driver',
};
const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active (not terminated)' },
  { value: 'ALL', label: 'All statuses' },
  { value: 'IN SERVICE', label: 'In Service' },
  { value: 'OUT OF SERVICE', label: 'Out of Service' },
  { value: 'TERMINATED', label: 'Terminated' },
];
const SEARCH_BY_OPTIONS = [
  { value: 'unitnumber', label: 'Unit #' },
  { value: 'unitlist', label: 'Unit list (comma separated)' },
  { value: 'drivername', label: 'Driver name' },
  { value: 'textmsgnumber', label: 'Cell / text #' },
  { value: 'driverlicnum', label: "Driver's license #" },
  { value: 'truckvin', label: 'Truck VIN' },
  { value: 'trucklicplate', label: 'Truck plate' },
  { value: 'truckownerssn', label: 'Owner SSN (last 4)' },
];
const BUCKET_LABELS: Record<string, string> = {
  driverless: 'Driverless trucks',
  truckless_mule: 'Truckless mule drivers',
  truckless_w2temp: 'Truckless W2 / temp drivers',
  muledriver: 'Mule drivers in service',
  dispatched: 'Dispatched today',
  notdispatched: 'Not dispatched today',
};
const bucketLabel = (bucket: string) =>
  bucket.startsWith('oos_type:') ? `Out of service: ${bucket.slice('oos_type:'.length)}` : BUCKET_LABELS[bucket] ?? bucket;

const asView = (v: string | null): UnitsView => (v && VIEW_VALUES.has(v) ? (v as UnitsView) : 'driver');

export default function SafetyUnits() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageComponents } = usePermissionsMode();

  // The drawer's gates are registered here so they are configurable in permissions mode.
  // `safety-system-table-survey` is the Safety Tables survey component: the Retention tab
  // opens that drawer, whose edit gate must resolve on this page too.
  const pageComponentsList = useMemo(() => [
    { id: 'safety-system-units-table', name: 'Safety Units Table' },
    { id: 'safety-system-unit-edit', name: 'Unit Edit' },
    { id: 'safety-system-payroll-sensitive', name: 'Payroll Sensitive Data' },
    { id: 'safety-system-table-survey', name: 'Driver Surveys' },
  ], []);
  useEffect(() => {
    setPageComponents(pageComponentsList);
    return () => setPageComponents([]);
  }, [pageComponentsList, setPageComponents]);

  // ---- URL state -----------------------------------------------------------
  const view = asView(searchParams.get('view'));
  const status = searchParams.get('status') || 'ACTIVE';
  const region = searchParams.get('region') || 'ALL';
  const includeMules = searchParams.get('include_mules') === '1';
  const q = searchParams.get('q') || '';
  const searchBy = searchParams.get('search_by') || 'unitnumber';
  const bucket = searchParams.get('bucket') || '';
  const openUnit = searchParams.get('open') || '';

  const setParams = useCallback((patch: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Search text is drafted locally and only committed to the URL on Search / Enter.
  const [draft, setDraft] = useState(q);
  useEffect(() => { setDraft(q); }, [q]);
  const commitSearch = () => setParams({ q: draft.trim(), open: null });

  // ---- Lookups -------------------------------------------------------------
  const [regions, setRegions] = useState<SafetyRegion[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    safetySystemService.regions(controller.signal)
      .then((rows) => { if (!controller.signal.aborted) setRegions(rows); })
      .catch(() => { /* region filter degrades to ALL */ });
    return () => controller.abort();
  }, []);
  const regionOptions = useMemo(() => regions.map((r) => ({ value: String(r.recno), label: r.label })), [regions]);
  const regionValues = useMemo(
    () => (region.toUpperCase() === 'ALL' ? [] : region.split(/[,\s]+/).filter((p) => /^\d+$/.test(p))),
    [region],
  );

  // ---- Data ----------------------------------------------------------------
  const [rows, setRows] = useState<UnitRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /** Load Request: the backend degrades the four settlement columns to null and says so. */
  const [settlementsError, setSettlementsError] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  // open=<unitnumber> (from the Hub) narrows the query to that unit so the row is always present.
  const listParams = useMemo<UnitsListParams>(() => ({
    view,
    status,
    region,
    include_mules: includeMules,
    q: openUnit || q,
    search_by: openUnit ? 'unitnumber' : searchBy,
    bucket,
  }), [view, status, region, includeMules, q, searchBy, bucket, openUnit]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setSettlementsError('');
    safetySystemService.listUnits(listParams, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        setRows(res.data ?? []);
        setSettlementsError(res.settlements_error || '');
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setRows([]);
        setError(e?.response?.data?.detail || 'Unable to load units. Retry or check your access with an administrator.');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [listParams, reloadTick]);

  // ---- Drawer --------------------------------------------------------------
  const [openRecno, setOpenRecno] = useState<number | null>(null);
  const autoOpened = useRef<string>('');
  useEffect(() => {
    if (!openUnit || loading || autoOpened.current === openUnit) return;
    const match = rows.find((r) => String(r.unitnumber) === openUnit && r.unitrecno);
    if (match?.unitrecno) {
      autoOpened.current = openUnit;
      setOpenRecno(match.unitrecno);
    }
  }, [openUnit, rows, loading]);

  const closeDrawer = () => {
    setOpenRecno(null);
    if (openUnit) setParams({ open: null });
  };

  const columns = unitsColumnsFor(view);
  const defaultHidden = unitsDefaultHiddenFor(view);
  const defaultSorting = useMemo(() => unitsDefaultSorting(view), [view]);
  const drawerTab = VIEW_DRAWER_TAB[view];

  return (
    <main className="flex h-[calc(100vh-7rem)] min-h-[450px] flex-col gap-2 overflow-hidden p-2 sm:p-4 text-gray-900 dark:text-gray-100">
      <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Safety Units</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Drivers and trucks by unit. Click a row to open the unit.</p>
        </div>
        <button
          type="button"
          onClick={() => setReloadTick((t) => t + 1)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight disabled:cursor-not-allowed disabled:text-disabled"
        >
          <ArrowPathIcon width={16} aria-hidden="true" />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </header>
      {error && <div role="alert" className="shrink-0 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{error}</div>}
      {!error && settlementsError && (
        <div role="status" className="shrink-0 rounded-md border border-warning bg-warning px-3 py-1.5 text-sm text-warning-fg">
          {settlementsError} — the Loans / Deferments / Escrow / Difference columns are blank for this load.
        </div>
      )}

      <FilterBar wrap label="" className="shrink-0">
        <FilterBar.Group label="View">
          <SingleSelect ariaLabel="View" value={view} onChange={(v) => setParams({ view: v })}
            options={VIEW_OPTIONS} size="sm" className="w-40" usePortal />
        </FilterBar.Group>
        <FilterBar.Group label="Status">
          <SingleSelect ariaLabel="Status" value={status} onChange={(v) => setParams({ status: v || 'ACTIVE' })}
            options={STATUS_OPTIONS} size="sm" className="w-40" usePortal disabled={!!bucket} />
        </FilterBar.Group>
        <FilterBar.Group label="Region">
          <MultiSelect value={regionValues} onChange={(vals) => setParams({ region: vals.length ? vals.join(',') : 'ALL' })}
            options={regionOptions} placeholder="All regions" size="sm" className="w-44" enableSearch usePortal
            showClearOption clearOptionLabel="All regions" />
        </FilterBar.Group>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={includeMules} onChange={(e) => setParams({ include_mules: e.target.checked ? '1' : null })}
            className="h-4 w-4 rounded border-rule-strong" />
          Include mules
        </label>
        <FilterBar.Divider />
        <FilterBar.Group label="Search by">
          <SingleSelect ariaLabel="Search by" value={searchBy} onChange={(v) => setParams({ search_by: v })}
            options={SEARCH_BY_OPTIONS} size="sm" className="w-52" usePortal />
        </FilterBar.Group>
        <input
          type="text"
          aria-label="Search value"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commitSearch(); }}
          placeholder={searchBy === 'unitlist' ? '1522, 1530, 1601' : 'Search…'}
          className="h-8 w-48 rounded-md border border-rule-strong bg-field px-2 text-sm"
        />
        <button type="button" onClick={commitSearch}
          className="inline-flex h-8 items-center gap-1 rounded-md bg-accent px-3 text-sm font-medium text-accent-fg hover:bg-accent-hover">
          <MagnifyingGlassIcon width={16} aria-hidden="true" />Search
        </button>
        {q && (
          <button type="button" onClick={() => setParams({ q: null })}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-rule px-2 text-sm hover:bg-highlight">
            <XMarkIcon width={14} aria-hidden="true" />Clear search
          </button>
        )}
        {bucket && (
          <span className="inline-flex items-center gap-1 rounded-full bg-info px-2.5 py-0.5 text-xs font-medium text-info-fg">
            {bucketLabel(bucket)}
            <button type="button" aria-label="Clear bucket filter" onClick={() => setParams({ bucket: null })} className="rounded-full hover:bg-highlight">
              <XMarkIcon width={14} aria-hidden="true" />
            </button>
          </span>
        )}
      </FilterBar>

      <PermissionsContainer componentId="safety-system-units-table" componentName="Safety Units Table" defaultHidden className="min-h-0 flex-1">
        <TanTable<UnitRow>
          data={rows}
          columns={columns}
          loading={loading}
          fillContainer
          cardTitle={VIEW_TITLES[view]}
          settingsKey={`safety-units-${view}`}
          fieldName={`safety-units-${view}`}
          searchPlaceholder="Filter results…"
          enableColumnFilters
          enableExcelExport
          enableShowHideCols
          enableRowsPerPage
          enableZebraStriping
          defaultSorting={defaultSorting}
          defaultColumnVisibility={defaultHidden}
          defaultPageSize={100}
          dateColumns={UNITS_DATE_COLUMNS}
          onRowClick={(r) => { if (r.unitrecno) setOpenRecno(r.unitrecno); }}
          getRowId={(r) => unitsRowId(view, r)}
        />
      </PermissionsContainer>

      <UnitDrawer
        isOpen={openRecno !== null}
        onClose={closeDrawer}
        unitrecno={openRecno}
        initialTab={drawerTab}
        onSaved={() => setReloadTick((t) => t + 1)}
      />
    </main>
  );
}
